import { useState, useMemo } from 'react';
import {
  useGetMemberSummaryQuery,
  useGetLatestRyderCupTeamsQuery,
  useGetTeamCompetitionsQuery,
  useGetEventTeamResultsByMemberQuery,
} from '../state/api';
import { selectAllMembers, selectAllTeamCompetitions } from '../state/entitySelectors';
import { useAppSelector } from '../hooks/reduxHooks';
import RyderCupMemberStats from './ryder-cup-member-stats.tsx';
import type { Member, CurrentRyderCupTeamMember, TeamCompetitorMemberSummary } from '../types/api.types';
import {
  buildMemberPointsMap,
  getMembersInRecentCompetitions,
  initializeMemberStats,
  updateMemberAppearances,
  calculatePointsPercentages,
} from './member-stats.utils';
import {
  filterAndSortMemberStats,
  type MemberFilter,
  type SortField,
  type SortOrder,
} from './member-stats-filters.utils';

export interface MemberRyderCupStats {
  member: Member;
  teamInfo: CurrentRyderCupTeamMember | undefined;
  totalPointsScored: number;
  totalAvailablePoints: number;
  pointsPercentage: number;
  usaAppearances: number;
  usaWins: number;
  usaPointsScored: number;
  usaAvailablePoints: number;
  europeAppearances: number;
  europeWins: number;
  europePointsScored: number;
  europeAvailablePoints: number;
  isActiveMember: boolean;
}

const RyderCupMemberStatsContainer = () => {
  // State management for filtering and sorting
  const [memberFilter, setMemberFilter] = useState<MemberFilter>('all');
  const [sortBy, setSortBy] = useState<SortField>('points');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');

  // Fetch data with RTK Query
  useGetMemberSummaryQuery();
  useGetTeamCompetitionsQuery({ withCompetitors: true });

  // Use entity selector for optimized access to normalized data
  const lstMembers = useAppSelector(selectAllMembers);
  const teamCompetitions = useAppSelector((state) => selectAllTeamCompetitions(state, true));
  const { data: latestRyderCupTeams = [] } = useGetLatestRyderCupTeamsQuery();

  // We need to fetch results for each competition, but we can't call hooks conditionally
  // or in a loop. Instead, we'll fetch all competition IDs first, then call hooks for
  // a fixed maximum number. RTK Query's skip parameter will prevent unnecessary fetches.
  const competitionIds = teamCompetitions.map((comp) => comp.id);
  const maxCompetitions = 20; // Adjust based on expected max Ryder Cups

  // Call hooks unconditionally for a fixed number of competitions
  const competitionResults = Array.from({ length: maxCompetitions }, (_, index) => {
    const compId = competitionIds[index];
    const { data } = useGetEventTeamResultsByMemberQuery(compId || '', {
      skip: !compId, // Skip the query if there's no competition at this index
    });
    return compId ? { competitionId: compId, results: data || [] } : null;
  }).filter((result): result is { competitionId: string; results: TeamCompetitorMemberSummary[] } => result !== null);

  // Build a map of member points across all competitions
  const memberPointsMap = useMemo(() => {
    return buildMemberPointsMap(competitionResults);
  }, [competitionResults]);

  // Calculate member statistics
  const memberStats = useMemo(() => {
    const statsMap = new Map<string, MemberRyderCupStats>();

    // Sort competitions by date (most recent first)
    const sortedCompetitions = [...teamCompetitions].sort((a, b) => {
      const dateA = new Date(a.eventDate);
      const dateB = new Date(b.eventDate);
      return dateB.getTime() - dateA.getTime();
    });

    // Get member IDs from latest Ryder Cup teams
    const latestTeamMemberIds = new Set(latestRyderCupTeams.map((m) => m.id));

    // Track which members competed in the last 2 competitions
    const membersInLast2Comps = getMembersInRecentCompetitions(sortedCompetitions, 2);

    // Initialize stats for all members
    lstMembers.forEach((member) => {
      const teamInfo = latestRyderCupTeams.find((t) => t.id === member.id);
      const isActiveMember = latestTeamMemberIds.has(member.id) || membersInLast2Comps.has(member.id);
      const memberPoints = memberPointsMap.get(member.id);

      const initialStats = initializeMemberStats(member, teamInfo, isActiveMember, memberPoints);
      statsMap.set(member.id, initialStats);
    });

    // Process team competitions to count appearances and wins by team
    teamCompetitions.forEach((comp) => {
      if (!comp.competitors) return;

      comp.competitors.forEach((competitor) => {
        const stats = statsMap.get(competitor.memberID);
        if (!stats) return;

        updateMemberAppearances(stats, competitor.team, comp.winner);
      });
    });

    // Calculate points percentages
    calculatePointsPercentages(statsMap);

    return Array.from(statsMap.values());
  }, [lstMembers, latestRyderCupTeams, teamCompetitions, memberPointsMap]);

  // Filter and sort member stats using extracted utility
  const filteredAndSortedStats = useMemo(
    () => filterAndSortMemberStats(memberStats, memberFilter, sortBy, sortOrder),
    [memberStats, memberFilter, sortBy, sortOrder]
  );

  // Handler for sort column changes
  const handleSort = (column: SortField) => {
    if (sortBy === column) {
      // Toggle sort order if clicking the same column
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // New column, default to descending
      setSortBy(column);
      setSortOrder('desc');
    }
  };

  return (
    <RyderCupMemberStats
      memberStats={filteredAndSortedStats}
      memberFilter={memberFilter}
      onFilterChange={setMemberFilter}
      sortBy={sortBy}
      sortOrder={sortOrder}
      onSort={handleSort}
    />
  );
};

export default RyderCupMemberStatsContainer;
